/**
 * Facility wallet ledger (ENG-27315).
 *
 * Opens from a row of the Wallet tab's facility table. A Dialog rather than a
 * drawer, matching the app's other detail surfaces (PaymentDetailModal,
 * VendorWalletModal) — drawers here are for filters and mobile nav. Widened to
 * `md` because the ledger is a four-column table, and full-screen on mobile.
 *
 * Transactions come straight from Gringotts through the gateway — VIP's
 * transactions endpoint is deferred (ADR 0023 §15).
 *
 * The Gringotts search requires a `transferType`, so "All" is two calls merged
 * client-side. See `trimToCompleteWindow` for why the merged list is trimmed
 * rather than shown whole.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { useWalletSummary, useWalletTransactions } from "../api/walletQueries";
import type { TransferType } from "../types/wallet";
import {
  formatEpochDate,
  mergeLedgerPages,
  paymentTypeCategory,
  readSummaryTotals,
  runningBalance,
  searchEndDate,
  toIsoDate,
  transactionDescription,
  trimToCompleteWindow,
  WALLET_HISTORY_START,
} from "../utils/walletLedger";
import { formatAmount } from "../utils/formatters";
import WalletTypeBadge from "../components/WalletTypeBadge";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import { useIsMobile } from "@/hooks/useIsMobile";
import { INVERTED_RANGE_MESSAGE, isInvertedRange } from "@/lib/dateRange";

/** Rows fetched per direction, per step. Facility wallets hold tens of rows. */
export const LEDGER_PAGE_SIZE = 25;

type DirectionFilter = "all" | TransferType;

export interface LedgerFacility {
  id: number;
  name: string;
  walletId: number | null;
  /**
   * Totals already fetched for the facility table (VIP's wallet read).
   * `balance` is the header's only source for Balance — the wallet's current
   * balance (ENG-28276). Credit / debit are the starting figures until the
   * windowed summary arrives, and the fallback if it yields nothing usable.
   */
  balance?: number;
  totalCredit?: number;
  totalDebit?: number;
}

interface WalletLedgerModalProps {
  open: boolean;
  onClose: () => void;
  facility: LedgerFacility | null;
}

export default function WalletLedgerModal({
  open,
  onClose,
  facility,
}: WalletLedgerModalProps) {
  const isMobile = useIsMobile();
  const [direction, setDirection] = useState<DirectionFilter>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pageSize, setPageSize] = useState(LEDGER_PAGE_SIZE);

  // A To before the From (ENG-28341). The pickers' min/max stop most of these,
  // but a typed date isn't bound by them.
  const rangeInvalid = isInvertedRange(startDate, endDate);
  // An inverted window has no meaningful ledger, so nothing is fetched for it.
  const walletId = rangeInvalid ? null : facility?.walletId ?? null;

  // Reset the view whenever a different facility is opened, so the previous
  // facility's filters and expanded window don't carry over.
  useEffect(() => {
    setDirection("all");
    setStartDate("");
    setEndDate("");
    setPageSize(LEDGER_PAGE_SIZE);
  }, [facility?.id]);

  // Gringotts rejects a request with no window, so an unset filter becomes the
  // widest sensible range rather than an absent parameter. The summary takes
  // the "To" day as is; the search needs the day after it (see searchEndDate).
  const range = useMemo(() => {
    const inclusiveEnd = endDate || toIsoDate(new Date());
    const summaryRange = { startDate: startDate || WALLET_HISTORY_START, endDate: inclusiveEnd };
    return { summary: summaryRange, search: { ...summaryRange, endDate: searchEndDate(inclusiveEnd) } };
  }, [startDate, endDate]);

  const summary = useWalletSummary(open ? walletId : null, range.summary);

  const credits = useWalletTransactions(
    open ? walletId : null,
    { transferType: "credit", pageNumber: 0, pageSize, ...range.search },
    { enabled: direction !== "debit" },
  );
  const debits = useWalletTransactions(
    open ? walletId : null,
    { transferType: "debit", pageNumber: 0, pageSize, ...range.search },
    { enabled: direction !== "credit" },
  );

  // Memoised because the `?? []` fallback would otherwise hand a fresh array to
  // the merge below on every render, making its useMemo recompute every time.
  const creditData = credits.data?.transactionsModelList;
  const debitData = debits.data?.transactionsModelList;
  // A disabled query keeps the last window's rows, so an inverted range drops
  // them here rather than showing transactions for dates the user moved off.
  const creditRows = useMemo(
    () => (rangeInvalid || direction === "debit" ? [] : creditData ?? []),
    [rangeInvalid, direction, creditData],
  );
  const debitRows = useMemo(
    () => (rangeInvalid || direction === "credit" ? [] : debitData ?? []),
    [rangeInvalid, direction, debitData],
  );

  const creditsHasMore =
    !rangeInvalid && direction !== "debit" && (credits.data?.totalCount ?? 0) > creditRows.length;
  const debitsHasMore =
    !rangeInvalid && direction !== "credit" && (debits.data?.totalCount ?? 0) > debitRows.length;

  const rows = useMemo(
    () =>
      trimToCompleteWindow(mergeLedgerPages(creditRows, debitRows), [
        { rows: creditRows, hasMore: creditsHasMore },
        { rows: debitRows, hasMore: debitsHasMore },
      ]),
    [creditRows, debitRows, creditsHasMore, debitsHasMore],
  );

  const isLoading =
    (direction !== "debit" && credits.isInitialLoading) ||
    (direction !== "credit" && debits.isInitialLoading);
  const isError =
    (direction !== "debit" && credits.isError) ||
    (direction !== "credit" && debits.isError);
  const hasMore = creditsHasMore || debitsHasMore;

  // Balance: the wallet's current balance (VIP), never the windowed summary — see WalletSummaryDTO (ENG-28276).
  const windowed = readSummaryTotals(summary.data);
  const totals = rangeInvalid
    ? { balance: undefined, credit: undefined, debit: undefined }
    : {
        balance: facility?.balance,
        credit: windowed.credit ?? facility?.totalCredit,
        debit: windowed.debit ?? facility?.totalDebit,
      };
  // With dates picked, Credit / Debit cover them but Balance does not.
  const hasDateFilter = startDate !== "" || endDate !== "";

  const handleDirectionChange = (_: unknown, next: DirectionFilter | null) => {
    // ToggleButtonGroup hands back null when the active button is clicked
    // again; keep the current filter rather than falling into an unfiltered
    // state the buttons can't represent.
    if (next === null) return;
    setDirection(next);
    setPageSize(LEDGER_PAGE_SIZE);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { height: isMobile ? "100%" : "80vh" } }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pb: 1 }}
      >
          <Box>
            <Typography fontWeight={700} fontSize="16px">
              {facility?.name ?? "Wallet ledger"}
            </Typography>
            {/* The subtitle names what the panel is; with no facility the title
                already says that, so showing both would just repeat itself. */}
            {facility?.name && (
              <Typography fontSize="12px" color="text.secondary">
                Wallet ledger
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column" }}>
        {/* Totals for the selected window */}
        <Box display="flex" gap={3} mb={2} flexWrap="wrap">
          <LedgerTotal
            label="Balance"
            value={totals.balance}
            color={V2_COLORS.primary}
            note={hasDateFilter && !rangeInvalid ? "Current, not for these dates" : undefined}
          />
          <LedgerTotal
            label="Credit"
            value={totals.credit}
            color={V2_COLORS.success}
          />
          <LedgerTotal
            label="Debit"
            value={totals.debit}
            color={V2_COLORS.error}
          />
        </Box>

        {/* Filters */}
        <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={direction}
            onChange={handleDirectionChange}
            aria-label="Direction"
          >
            <ToggleButton value="all" sx={{ textTransform: "none" }}>
              All
            </ToggleButton>
            <ToggleButton value="credit" sx={{ textTransform: "none" }}>
              Credit
            </ToggleButton>
            <ToggleButton value="debit" sx={{ textTransform: "none" }}>
              Debit
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: endDate || undefined }}
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPageSize(LEDGER_PAGE_SIZE);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
            error={rangeInvalid}
            helperText={rangeInvalid ? INVERTED_RANGE_MESSAGE : undefined}
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPageSize(LEDGER_PAGE_SIZE);
            }}
          />
        </Box>

        {/* Rows — flex column so the table takes the leftover height and scrolls
            inside itself, rather than a fixed maxHeight that can overflow the
            dialog and let the footer overlap the last rows. */}
        <Box flex={1} minHeight={0} display="flex" flexDirection="column">
          {isLoading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!isLoading && isError && (
            <Box py={5} textAlign="center">
              <Typography color="error" fontSize="14px">
                Couldn&apos;t load the ledger. Please try again.
              </Typography>
            </Box>
          )}

          {!isLoading && !isError && rows.length === 0 && (
            <Box py={5} textAlign="center">
              <Typography color="text.secondary" fontSize="14px">
                {rangeInvalid
                  ? "Pick a To date on or after the From date."
                  : "No transactions for this selection."}
              </Typography>
            </Box>
          )}

          {!isLoading && !isError && rows.length > 0 && (
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                border: "1px solid #E6E6E6",
                borderRadius: "8px",
                // Hints that there is more below: the shadow sits under the
                // header and lifts as the body scrolls beneath it.
                "& thead th": { boxShadow: "0 1px 0 #E6E6E6" },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Date", "Type", "Description", "Amount", "Balance"].map((heading, i) => (
                      <TableCell
                        key={heading}
                        align={i >= 3 ? "right" : "left"}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          // stickyHeader needs an opaque cell, or rows show through.
                          backgroundColor: "background.paper",
                        }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const category = paymentTypeCategory(row.transactionContexts);
                    const description = transactionDescription(row.transactionContexts);
                    const isCredit = row.direction === "credit";
                    const balance = runningBalance(row);
                    return (
                      <TableRow key={`${row.direction}-${row.id}`}>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                          {formatEpochDate(row.transactionDate)}
                        </TableCell>
                        <TableCell>
                          <WalletTypeBadge category={category.key} label={category.label} />
                        </TableCell>
                        <TableCell>
                          {/* The chip already names the type, so this column
                              carries only the documents the movement relates to. */}
                          <Typography fontSize="13px" color={description ? undefined : "text.secondary"}>
                            {description || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            component="span"
                            fontSize="13px"
                            fontWeight={600}
                            color={isCredit ? V2_COLORS.success : V2_COLORS.error}
                          >
                            {isCredit ? "+" : "−"}
                            {formatAmount(row.sourceAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                          {balance == null ? "—" : formatAmount(balance)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {!isLoading && !isError && hasMore && (
          // flexShrink: 0 — the footer must keep its height and push the table
          // to shrink, not be compressed into the rows above it.
          <Box display="flex" justifyContent="center" pt={2} flexShrink={0}>
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() => setPageSize((size) => size + LEDGER_PAGE_SIZE)}
            >
              Load more
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LedgerTotal({
  label,
  value,
  color,
  note,
}: {
  label: string;
  value: number | undefined;
  color: string;
  note?: string;
}) {
  return (
    <Box>
      <Typography
        fontSize="11px"
        fontWeight={500}
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
      >
        {label}
      </Typography>
      <Typography fontSize="16px" fontWeight={700} color={color}>
        {value == null ? "—" : formatAmount(value)}
      </Typography>
      {note && (
        <Typography fontSize="11px" color="text.secondary">
          {note}
        </Typography>
      )}
    </Box>
  );
}
